import { useCallback, useEffect, useRef, useState } from 'react';
import { pixelHeightToGridUnitsFloor } from '../gridMetrics';

const VIEWPORT_BOTTOM_PADDING_PX = 16;
const RESIZE_DEBOUNCE_MS = 80;

function measureAvailableGridHeightPx() {
  if (typeof window === 'undefined') return null;

  const wrapper = document.querySelector('.mainExchangelayout .wrapperLayout');
  if (!wrapper) return null;

  const top = wrapper.getBoundingClientRect().top;
  const available = window.innerHeight - top - VIEWPORT_BOTTOM_PADDING_PX;
  return available > 240 ? available : null;
}

/**
 * Grid rows available below the workspace tabs — floored so the page does not scroll.
 */
export function useTradeGridViewportFill({ enabled = true } = {}) {
  const [fillBottomH, setFillBottomH] = useState(null);
  const lastHRef = useRef(null);
  const timeoutRef = useRef(null);
  const rafRef = useRef(null);

  const measure = useCallback(() => {
    const availablePx = measureAvailableGridHeightPx();
    if (availablePx == null) return;

    const nextH = pixelHeightToGridUnitsFloor(availablePx);
    const prevH = lastHRef.current;
    if (prevH !== null && Math.abs(nextH - prevH) < 0.1) return;

    lastHRef.current = nextH;
    setFillBottomH(nextH);
  }, []);

  const scheduleMeasure = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(measure);
    }, RESIZE_DEBOUNCE_MS);
  }, [measure]);

  useEffect(() => {
    if (!enabled) {
      setFillBottomH(null);
      lastHRef.current = null;
      return undefined;
    }

    scheduleMeasure();
    window.addEventListener('resize', scheduleMeasure);

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(scheduleMeasure) : null;

    const wrapper = document.querySelector('.mainExchangelayout .wrapperLayout');
    const tabs = document.querySelector('.exchangeTabs');
    if (resizeObserver) {
      if (wrapper) resizeObserver.observe(wrapper);
      if (tabs) resizeObserver.observe(tabs);
    }

    return () => {
      window.removeEventListener('resize', scheduleMeasure);
      resizeObserver?.disconnect();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [enabled, scheduleMeasure]);

  return fillBottomH;
}
